import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Pager,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { LoadPanel } from 'devextreme-react/load-panel';
import data from "../../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';

function DetailTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const detailGrid = useRef(null);
    const rateDetailGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedItemKeys, setSelectedItemKeys] = useState([]);
    const [selectedDateRowId, setSelectedDateRowId] = useState(null);
    const [selectedDetailKeys, setSelectedDetailKeys] = useState([]);
    const gridKey = "D1E2F3A4-B5C6-7D8E-9F0A-1B2C3D4E5F6A";
    const detailGridKey = "E2F3A4B5-C6D7-8E9F-0A1B-2C3D4E5F6A7B";

    const position = { of: '#accordion' };

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    // ============================================================
    // First Grid: Rate Table Dates
    // ============================================================
    const loadState = useCallback(() => {
        return null;
    }, []);

    const saveState = useCallback((state) => {
    }, []);

    const dataSource = useMemo(() => {
        if (!props.headerId || props.headerId === '00000000-0000-0000-0000-000000000000') {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDate.GetRateTableDates}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDate.InsertRateTableDate}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDate.UpdateRateTableDate}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDate.DeleteRateTableDate}`,
                loadParams: {
                    rateTableHeaderRowId: props.headerId
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };

                    if (method === "insert" || method === "update") {
                        ajaxOptions.headers['Content-Type'] = 'application/json';
                    }

                    if (method === "insert") {
                        try {
                            let valuesObj = {};
                            if (ajaxOptions.data && ajaxOptions.data.values) {
                                if (typeof ajaxOptions.data.values === 'string') {
                                    valuesObj = JSON.parse(ajaxOptions.data.values);
                                } else {
                                    valuesObj = ajaxOptions.data.values;
                                }
                            }

                            const payload = {
                                RowId: '00000000-0000-0000-0000-000000000000',
                                RRateTableHeader: props.headerId,
                                EffectiveDate: valuesObj.EffectiveDate || null,
                                Description: valuesObj.Description || null,
                                Comment: valuesObj.Comment || null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing insert values:", e);
                        }
                    }

                    if (method === "update") {
                        try {
                            let valuesObj = {};
                            let key = null;

                            if (ajaxOptions.data) {
                                key = ajaxOptions.data.key;
                                if (ajaxOptions.data.values) {
                                    if (typeof ajaxOptions.data.values === 'string') {
                                        valuesObj = JSON.parse(ajaxOptions.data.values);
                                    } else {
                                        valuesObj = ajaxOptions.data.values;
                                    }
                                }
                            }

                            const payload = {
                                RowId: key,
                                RRateTableHeader: props.headerId,
                                EffectiveDate: valuesObj.EffectiveDate !== undefined ? valuesObj.EffectiveDate : null,
                                Description: valuesObj.Description !== undefined ? valuesObj.Description : null,
                                Comment: valuesObj.Comment !== undefined ? valuesObj.Comment : null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing update values:", e);
                        }
                    }
                },
                onInserted: function (e) {
                    notify("Rate Table Date inserted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onUpdated: function (key, updatedValues) {
                    notify("Rate Table Date updated successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onInsertError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to insert Rate Table Date.";
                    notify(message, "error", 5000);
                },
                onUpdateError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to update Rate Table Date.";
                    notify(message, "error", 5000);
                },
                onRemoved: function (key) {
                    notify("Rate Table Date deleted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onRemoveError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to delete Rate Table Date.";
                    notify(message, "error", 5000);
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [props.headerId, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedItemKeys(e.selectedRowKeys);
        // When a date row is selected, update the selectedDateRowId for the second grid
        if (e.selectedRowKeys && e.selectedRowKeys.length > 0) {
            setSelectedDateRowId(e.selectedRowKeys[0]);
        } else {
            setSelectedDateRowId(null);
        }
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RRateTableHeader = props.headerId;
        e.data.EffectiveDate = null;
        e.data.Description = '';
        e.data.Comment = '';
    }, [props.headerId]);

    const onRowInserted = useCallback((e) => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().selectRows([e.key], true);
        }
    }, []);

    const onRowUpdating = useCallback((e) => {
    }, []);

    const onRowUpdated = useCallback(() => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().refresh();
        }
    }, []);

    const onEditingStart = useCallback((e) => {
        if (e.key != null && e.key != undefined && !detailGrid.current?.instance()?.isRowSelected(e.key)) {
            detailGrid.current.instance().selectRows([e.key], false);
        }
    }, []);

    const onEditCanceled = useCallback(() => {
    }, []);

    const onContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    }, []);

    // ============================================================
    // Second Grid: Rate Table Details (based on selected Date row)
    // ============================================================
    const loadStateDetail = useCallback(() => {
        return null;
    }, []);

    const saveStateDetail = useCallback((state) => {
    }, []);

    const detailDataSource = useMemo(() => {
        if (!selectedDateRowId) {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDetail.GetRateTableDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDetail.InsertRateTableDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDetail.UpdateRateTableDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.RateTableDetail.DeleteRateTableDetail}`,
                loadParams: {
                    rateTableDateRowId: selectedDateRowId
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };

                    if (method === "insert" || method === "update") {
                        ajaxOptions.headers['Content-Type'] = 'application/json';
                    }

                    if (method === "insert") {
                        try {
                            let valuesObj = {};
                            if (ajaxOptions.data && ajaxOptions.data.values) {
                                if (typeof ajaxOptions.data.values === 'string') {
                                    valuesObj = JSON.parse(ajaxOptions.data.values);
                                } else {
                                    valuesObj = ajaxOptions.data.values;
                                }
                            }

                            const payload = {
                                RowId: '00000000-0000-0000-0000-000000000000',
                                RRateTableDate: selectedDateRowId,
                                StartingQuantity: valuesObj.StartingQuantity ?? null,
                                Rate: valuesObj.Rate ?? null,
                                Comment: valuesObj.Comment || null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing insert values:", e);
                        }
                    }

                    if (method === "update") {
                        try {
                            let valuesObj = {};
                            let key = null;

                            if (ajaxOptions.data) {
                                key = ajaxOptions.data.key;
                                if (ajaxOptions.data.values) {
                                    if (typeof ajaxOptions.data.values === 'string') {
                                        valuesObj = JSON.parse(ajaxOptions.data.values);
                                    } else {
                                        valuesObj = ajaxOptions.data.values;
                                    }
                                }
                            }

                            const payload = {
                                RowId: key,
                                RRateTableDate: selectedDateRowId,
                                StartingQuantity: valuesObj.StartingQuantity !== undefined ? valuesObj.StartingQuantity : null,
                                Rate: valuesObj.Rate !== undefined ? valuesObj.Rate : null,
                                Comment: valuesObj.Comment !== undefined ? valuesObj.Comment : null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing update values:", e);
                        }
                    }
                },
                onInserted: function (e) {
                    notify("Rate Table Detail inserted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (rateDetailGrid.current?.instance) {
                            rateDetailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onUpdated: function (key, updatedValues) {
                    notify("Rate Table Detail updated successfully.", "success", 3000);
                    setTimeout(() => {
                        if (rateDetailGrid.current?.instance) {
                            rateDetailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onInsertError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to insert Rate Table Detail.";
                    notify(message, "error", 5000);
                },
                onUpdateError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to update Rate Table Detail.";
                    notify(message, "error", 5000);
                },
                onRemoved: function (key) {
                    notify("Rate Table Detail deleted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (rateDetailGrid.current?.instance) {
                            rateDetailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onRemoveError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to delete Rate Table Detail.";
                    notify(message, "error", 5000);
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [selectedDateRowId, accessToken]);

    const onDetailSelectionChanged = useCallback((e) => {
        setSelectedDetailKeys(e.selectedRowKeys);
    }, []);

    const onDetailInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RRateTableDate = selectedDateRowId;
        e.data.StartingQuantity = 0;
        e.data.Rate = 0;
        e.data.Comment = '';
    }, [selectedDateRowId]);

    const onDetailRowInserted = useCallback((e) => {
        if (rateDetailGrid.current?.instance) {
            rateDetailGrid.current.instance().selectRows([e.key], true);
        }
    }, []);

    const onDetailRowUpdating = useCallback((e) => {
    }, []);

    const onDetailRowUpdated = useCallback(() => {
        if (rateDetailGrid.current?.instance) {
            rateDetailGrid.current.instance().refresh();
        }
    }, []);

    const onDetailEditingStart = useCallback((e) => {
        if (e.key != null && e.key != undefined && !rateDetailGrid.current?.instance()?.isRowSelected(e.key)) {
            rateDetailGrid.current.instance().selectRows([e.key], false);
        }
    }, []);

    const onDetailEditCanceled = useCallback(() => {
    }, []);

    const onDetailContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    }, []);

    // Reset the second grid's loaded flag when the selected date changes
    useEffect(() => {
        if (rateDetailGrid.current?.instance) {
            rateDetailGrid.current.instance()._hasLoaded = false;
        }
    }, [selectedDateRowId]);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />

            {/* First Grid: Rate Table Dates */}
            <h6 style={{ margin: '10px 0 5px 0', fontWeight: 'bold' }}>Rate Table Dates</h6>
            <DataGrid
                dataSource={dataSource}
                ref={detailGrid}
                selectedRowKeys={selectedItemKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                onInitNewRow={onInitNewRow}
                onRowInserted={onRowInserted}
                onRowUpdating={onRowUpdating}
                onRowUpdated={onRowUpdated}
                onEditingStart={onEditingStart}
                onEditCanceled={onEditCanceled}
                onContentReady={onContentReady}
                height="35vh"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={true}
                    allowUpdating={true}
                    allowDeleting={true}
                    newRowPosition="last"
                    confirmDelete={true}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this Rate Table Date?" }}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} />
                <Column dataField="RRateTableHeader" caption="Header ID" visible={false} />
                <Column
                    dataField="EffectiveDate"
                    caption="Effective Date"
                    dataType="date"
                    format="MM/dd/yyyy"
                    allowSorting={false}
                />
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="CreationDateTime"
                    caption="Creation Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="CreationUserId"
                    caption="Created By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateDateTime"
                    caption="Last Update Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateUserId"
                    caption="Last Updated By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>

            {/* Second Grid: Rate Table Details */}
            <h6 style={{ margin: '15px 0 5px 0', fontWeight: 'bold' }}>Rate Table Details</h6>
            <DataGrid
                dataSource={detailDataSource}
                ref={rateDetailGrid}
                selectedRowKeys={selectedDetailKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onDetailSelectionChanged}
                onInitNewRow={onDetailInitNewRow}
                onRowInserted={onDetailRowInserted}
                onRowUpdating={onDetailRowUpdating}
                onRowUpdated={onDetailRowUpdated}
                onEditingStart={onDetailEditingStart}
                onEditCanceled={onDetailEditCanceled}
                onContentReady={onDetailContentReady}
                height="35vh"
                noDataText={selectedDateRowId ? "No details found" : "Select a Rate Table Date row above"}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadStateDetail}
                    storageKey={detailGridKey}
                    customSave={saveStateDetail}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={!!selectedDateRowId}
                    allowUpdating={!!selectedDateRowId}
                    allowDeleting={!!selectedDateRowId}
                    newRowPosition="last"
                    confirmDelete={true}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this Rate Table Detail?" }}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} />
                <Column dataField="RRateTableDate" caption="Date ID" visible={false} />
                <Column
                    dataField="RecordNumber"
                    caption="Record #"
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column
                    dataField="StartingQuantity"
                    caption="Starting Quantity"
                    dataType="number"
                    format="#,##0.00"
                    allowSorting={false}
                />
                <Column
                    dataField="Rate"
                    caption="Rate"
                    dataType="number"
                    format="#,##0.00"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="CreationDateTime"
                    caption="Creation Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="CreationUserId"
                    caption="Created By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateDateTime"
                    caption="Last Update Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateUserId"
                    caption="Last Updated By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(DetailTab);
